import { useEffect, useState } from "react";
import { MapPin, CalendarHeart } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Reveal } from "./Reveal";

const STATUS = {
  soldout: { label: "Sold out", cls: "text-navy-faint bg-navy/5" },
  announced: { label: "In arrivo", cls: "text-ocean bg-ocean/15" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

export const Events = () => {
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <section id="events" className="px-5 sm:px-8 lg:px-12 py-20 sm:py-28" data-testid="events">
      <Reveal className="mb-12 text-center">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-green-deep">Prossime date</p>
        <h2 className="font-serif tracking-tightest text-4xl sm:text-6xl leading-[1.0] text-navy">
          Dove ci vediamo.
        </h2>
      </Reveal>

      {loaded && events.length === 0 && (
        <Reveal>
          <div className="mx-auto flex max-w-2xl flex-col items-center rounded-[2rem] glass px-8 py-16 text-center" data-testid="events-empty">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-lavender/15 text-lavender-deep">
              <CalendarHeart size={26} />
            </div>
            <p className="font-serif text-2xl text-navy">Prossime date in arrivo.</p>
            <p className="mt-3 max-w-md text-navy-soft">
              Il calendario è in aggiornamento. Seguici sui social per non perdere l’annuncio della prossima tappa.
            </p>
          </div>
        </Reveal>
      )}

      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        {events.map((ev, i) => {
          const st = STATUS[ev.status];
          return (
            <Reveal key={ev.id} delay={i * 0.06}>
              <div className="group overflow-hidden rounded-3xl glass transition-transform duration-500 hover:-translate-y-1" data-testid={`event-row-${i}`}>
                {ev.image && (
                  <img
                    src={mediaUrl(ev.image)}
                    alt={ev.venue}
                    className="h-52 w-full object-cover sm:h-64"
                    data-testid={`event-image-${i}`}
                  />
                )}
                <div className="grid grid-cols-1 items-center gap-4 p-6 sm:grid-cols-12 sm:p-7">
                  <div className="sm:col-span-3">
                    <p className="font-serif text-2xl text-navy group-hover:text-lavender-deep transition-colors duration-300">
                      {fmtDate(ev.date)}
                    </p>
                    {ev.time && <p className="text-sm text-navy-faint mt-1">ore {ev.time}</p>}
                  </div>
                  <div className="sm:col-span-6">
                    <p className="text-xl text-navy">{ev.venue}</p>
                    <p className="mt-1 flex items-center gap-2 text-navy-soft">
                      <MapPin size={15} className="text-lavender-deep" /> {ev.city}
                    </p>
                  </div>
                  <div className="sm:col-span-3 sm:text-right">
                    {ev.ticket_url && ev.status !== "soldout" ? (
                      <a href={ev.ticket_url} target="_blank" rel="noreferrer" data-testid={`event-ticket-${i}`}
                        className="inline-block rounded-full bg-lavender px-5 py-2.5 text-sm font-medium text-white hover:bg-lavender-deep transition-colors duration-300">
                        Scopri di più
                      </a>
                    ) : st ? (
                      <span className={`inline-block rounded-full px-4 py-2 text-sm font-medium ${st.cls}`}>{st.label}</span>
                    ) : null}
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};
